import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Leaf, Bird, Camera, MapPin, Sparkle, Microphone,
  CalendarDots, Export, ChartBar, Check, Star, ShieldCheck, ArrowRight
} from "@phosphor-icons/react";

const features = [
  { icon: Camera, title: "Photo Journals", desc: "Document wildlife with beautiful photo entries and automatic compression" },
  { icon: Sparkle, title: "AI Species ID", desc: "Identify birds, plants, fungi & wildlife instantly from your photos" },
  { icon: Microphone, title: "Voice Notes", desc: "Record field observations hands-free with AI transcription" },
  { icon: MapPin, title: "Interactive Maps", desc: "Pin sightings on a map with GPS tagging and offline support" },
  { icon: CalendarDots, title: "Calendar View", desc: "Browse your nature observations organized by date" },
  { icon: ChartBar, title: "Statistics", desc: "Track species counts, sighting trends, and personal records" },
  { icon: Bird, title: "Species Gallery", desc: "Build your personal field guide with every species you find" },
  { icon: Export, title: "PDF Export", desc: "Export beautiful reports, CSV data, or full JSON backups" },
];

const included = [
  "Unlimited journal entries & photos",
  "AI species identification (birds, plants, insects, fungi, wildlife)",
  "Voice note recording with AI transcription",
  "Interactive map with GPS tagging",
  "Calendar browsing view",
  "Species gallery & statistics dashboard",
  "PDF, JSON & CSV export",
  "13+ entry templates + custom templates",
  "Template marketplace",
  "eBird hotspot integration",
  "Offline mode with PWA support",
  "Lifetime updates — free forever",
];

const testimonials = [
  { text: "Finally, a nature journal that understands what birders need. The AI identification saves me hours.", author: "Field Naturalist" },
  { text: "I use this on every hike. Voice notes + GPS tagging means I never forget a sighting.", author: "Trail Walker" },
  { text: "The species gallery is like my own personal field guide. Worth every penny.", author: "Wildlife Photographer" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-black/40 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf size={24} weight="duotone" className="text-[#55624F]" />
            <span className="font-['Cormorant_Garamond'] text-lg font-bold text-white">Nature Journal</span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Button
                onClick={() => navigate("/")}
                data-testid="go-to-app-btn"
                size="sm"
                className="bg-[#55624F] hover:bg-[#3d4738] text-white rounded-xl font-['Manrope'] text-xs sm:text-sm"
              >
                Open App
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={() => navigate("/signup")}
                  data-testid="landing-signup-btn"
                  className="bg-[#55624F] hover:bg-[#424D3D] text-white rounded-xl font-['Manrope'] text-xs sm:text-sm"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C3E2D]/5 to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 pt-12 sm:pt-20 pb-12 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-black/30 text-white px-3 py-1.5 rounded-full text-xs sm:text-sm font-['Manrope'] font-medium mb-4 sm:mb-6">
                <Bird size={16} weight="duotone" /> Your digital field notebook
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight drop-shadow-lg">
                Document Nature with <span className="text-[#D4C9A8]">AI-Powered</span> Insight
              </h1>
              <p className="mt-4 sm:mt-6 text-sm sm:text-base text-white/80 font-['Manrope'] leading-relaxed max-w-lg mx-auto lg:mx-0">
                Photograph wildlife, identify species with AI, record voice notes, and build a beautiful
                personal archive of your nature observations.
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3">
                <Button
                  onClick={() => navigate(user ? "/" : "/signup")}
                  data-testid="hero-cta-btn"
                  className="bg-[#55624F] hover:bg-[#424D3D] text-white text-sm sm:text-base px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-['Manrope'] font-semibold h-auto w-full sm:w-auto"
                >
                  {user ? "Open Journal" : "Start Journaling — $29.99"}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
                <span className="text-xs sm:text-sm text-white font-['Manrope']">One-time purchase. Lifetime access.</span>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border-2 border-[#E8E6E1] shadow-xl">
                <img
                  src={require("../assets/hero-mockingbird.jpg")}
                  alt="Northern Mockingbird perched on branch"
                  className="w-full h-[200px] sm:h-[300px] lg:h-[380px] object-cover"
                />
              </div>
              <div className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg border border-[#E8E6E1] px-4 py-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#55624F]/10 flex items-center justify-center">
                  <Sparkle size={16} className="text-[#55624F]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#2C3E2D] font-['Manrope']">AI Identified</p>
                  <p className="text-[10px] text-[#7C8068] font-['Manrope']">Northern Mockingbird — 97%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/20 bg-black/30">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:py-5 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-white/80 font-['Manrope']">
          <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-white" />Secure Stripe payments</span>
          <span className="flex items-center gap-1.5"><Star size={16} weight="fill" className="text-[#55624F]" />Built for naturalists</span>
          <span className="flex items-center gap-1.5"><Check size={16} weight="bold" className="text-white" />No subscription — pay once</span>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-white text-center mb-3 drop-shadow-lg">
          Everything a naturalist needs
        </h2>
        <p className="text-sm text-white/70 font-['Manrope'] text-center mb-8 sm:mb-12 max-w-md mx-auto">
          From field observations to AI-powered species identification — all in your pocket.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-[#E8E6E1] hover:shadow-md transition-shadow"
              data-testid={`feature-card-${i}`}
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-[#55624F]/10 flex items-center justify-center mb-3 sm:mb-4">
                <f.icon size={20} weight="duotone" className="text-[#55624F]" />
              </div>
              <h3 className="font-['Manrope'] font-semibold text-[#2C3E2D] text-sm sm:text-base mb-1">{f.title}</h3>
              <p className="text-xs sm:text-sm text-[#7C8068] font-['Manrope'] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-[#2C3E2D] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-white text-center mb-8 sm:mb-12">
            Loved by nature enthusiasts
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 sm:p-6 border border-white/10">
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} size={14} weight="fill" className="text-[#55624F]" />)}
                </div>
                <p className="text-sm text-white/90 font-['Manrope'] leading-relaxed mb-4">"{t.text}"</p>
                <p className="text-xs text-white/60 font-['Manrope'] font-medium">— {t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16" id="pricing">
        <div className="max-w-lg mx-auto">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-white text-center mb-2 drop-shadow-lg">
            Simple, honest pricing
          </h2>
          <p className="text-sm text-white/70 font-['Manrope'] text-center mb-8">
            No subscriptions. No hidden fees. Just one payment.
          </p>
          <div className="bg-white rounded-2xl border-2 border-[#55624F] overflow-hidden shadow-lg">
            <div className="bg-[#55624F] text-white text-center py-3 text-xs sm:text-sm font-['Manrope'] font-semibold tracking-wide uppercase">
              Lifetime Access
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-[#2C3E2D] font-['Cormorant_Garamond']">$29.99</span>
                  <span className="text-[#7C8068] font-['Manrope'] text-sm">one-time</span>
                </div>
                <p className="text-[#7C8068] text-xs sm:text-sm font-['Manrope'] mt-2">Pay once, use forever. All future updates included.</p>
              </div>
              <ul className="space-y-2.5 mb-8">
                {included.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs sm:text-sm font-['Manrope'] text-[#2C3E2D]">
                    <Check size={16} weight="bold" className="text-[#55624F] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(user ? "/pricing" : "/signup")}
                data-testid="pricing-cta-btn"
                className="w-full bg-[#55624F] hover:bg-[#424D3D] text-white py-3 rounded-xl font-['Manrope'] font-semibold h-auto text-sm sm:text-base"
              >
                {user ? "Go to Checkout" : "Get Started"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/20 py-6 sm:py-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-white/70 text-xs sm:text-sm font-['Manrope']">
          <Leaf size={16} weight="duotone" className="text-[#55624F]" />
          Nature Journal &copy; {new Date().getFullYear()}
        </div>
        <a
          href="https://conniekelts.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-xs sm:text-sm text-white/60 hover:text-white transition-colors font-['Manrope']"
          data-testid="return-to-site-link"
        >
          <img src={require("../assets/ck-design-avatar.png")} alt="" className="w-8 h-8 rounded-full object-cover border border-white/30 bg-white" />
          &larr; Return to CK Design
        </a>
      </footer>
    </div>
  );
}
